import { PriceOption } from '../../domain'
import { defineAll } from '../../util/definedAll'
import {
  CheckoutMethod,
  ConfigurationState,
  DocumentConfig,
  DocumentType,
  FeatureToggles,
  ManagementType,
  PaymentScheduleType,
  ProductConfig,
  ProductDocument,
  Scheme,
  StaticDocument,
} from './config'

export const getContentBaseUrl = (state: ConfigurationState): string =>
  state.config.contentBaseUrl

export const getExposuresContentBaseUrl =
  (schemeCode: string) =>
  (state: ConfigurationState): string =>
    `${state.config.contentBaseUrl}/${schemeCode}/exposures`

export const getOptionalCoversContentBaseUrl =
  (schemeCode: string) =>
  (state: ConfigurationState): string =>
    `${state.config.contentBaseUrl}/${schemeCode}/optional`

export const getOptionalCoverLink = (
  contentBaseUrl: string,
  wordingVersion: string,
  { insurer, coverGroup }: Pick<PriceOption, 'insurer' | 'coverGroup'>,
) => `${contentBaseUrl}/${wordingVersion}/${insurer}.${coverGroup}.html`

export const getHelpLink =
  (name: string | undefined) =>
  (state: ConfigurationState): string | undefined =>
    name ? `${state.config.contentBaseUrl}/questions/${name}.html` : undefined

export const getSchemes = (state: ConfigurationState) => state.config.schemes

export const getSchemeByCode = (schemeCode: string) => (state: ConfigurationState) => {
  const scheme = state.config.schemes.find((pl) => pl.schemeCode === schemeCode)
  if (!scheme) {
    throw new Error(`No scheme configured for the scheme code ${schemeCode}`)
  }
  return scheme
}

export const getProduct = (schemeCode: string) => (state: ConfigurationState) =>
  state.config.productConfig[schemeCode]

export const getProducts = (state: ConfigurationState): Record<string, ProductConfig> =>
  state.config.productConfig

export const getStaticDocuments =
  (schemeCode: string) =>
  (state: ConfigurationState): StaticDocument[] =>
    getProduct(schemeCode)(state)
      ?.documents.filter((d) => d.type === DocumentType.STATIC)
      .map((document) => ({
        ...document,
        type: DocumentType.STATIC,
      })) ?? []

export const getFeatureToggles = ({
  config,
}: ConfigurationState): Partial<FeatureToggles> => config.features ?? {}

export const getProposalTrackingNames = ({ config }: ConfigurationState) =>
  config.proposalTracking ?? []

export const getCoreAssetType = ({ assetTypes }: ProductConfig) => {
  const assetType = assetTypes.find((it) => it.core)

  if (!assetType) {
    throw new Error('No core asset for product')
  }
  return assetType
}

export const getFormats = ({ config }: ConfigurationState) => config.formats

export const getAccountInputs = ({ config }: ConfigurationState) => config.account ?? []

export const getRetrieve = ({ config }: ConfigurationState) => config.retrieve

export const getFooter = ({ config }: ConfigurationState) => config.footer ?? []

export const isProductDocument = (doc: DocumentConfig): doc is ProductDocument =>
  doc.type === DocumentType.PRODUCT

export const isSchemeHidden = (scheme: Scheme) =>
  Boolean(scheme.hidden || scheme.disabled)

export const getPromotionDisclosureConfig = (product: ProductConfig) =>
  product.disclosures.find((it) => it.type === ManagementType.PROMOTION)

export const isPaymentScheduleType = (value: string): value is PaymentScheduleType =>
  defineAll<PaymentScheduleType>()('SINGLE_PAYMENT', 'MONTHLY').includes(
    value as PaymentScheduleType,
  )

export const isCheckoutMethod = (value: string | null): value is CheckoutMethod =>
  defineAll<CheckoutMethod>()('DIRECT_DEBIT', 'DUAL', 'CARD').includes(
    value as CheckoutMethod,
  )
